import React, { useEffect } from 'react';
import moment from 'moment';
import { useMutation } from '@apollo/client';
import useDashboardSelector from '../../../redux/selectorHooks/useDashboardSelector';
import { assertNotNil } from '../../../utils/types';
import {
  DASHBOARD_WORKSPACE_NAME_MAX_LENGTH,
  DESCRIPTION_MAX_LENGTH,
  IS_SCREENSHOT_MODE,
} from '../../../config/constants';
import { EditableTextInput } from '../../../components/Form';
import {
  ConversationThreadEntityType,
  EntityRole,
  IDashboardRefreshMutation,
  IDashboardRefreshMutationVariables,
} from '../../../utils/apollo/graphqlSchema';
import { DASHBOARD_REFRESH } from '../../../utils/apollo/dashboards';
import printGraphqlErrors from '../../../utils/apollo/printGraphqlErrors';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import {
  dashboardCustomizationActivate,
  dashboardInstanceToggleFavorite,
} from '../../../redux/slices/dashboards/dashboardInstance';
import { areAllAnalysesLoadedSelector } from '../../../redux/selectors/loadingAnalyses';
import { PageHeaderDivider } from '../../../components/PageHeader';
import { useDashboardEntityPermission } from '../../../utils/hooks/permissionHooks/useDashboardEntityPermission';
import * as DashboardInstanceUI from './DashboardInstanceUI';
import DashboardPagePath from './components/DashboardPagePath';
import DashboardOptions from './components/DashboardOptions';
import DashboardRefreshInfo from './components/DashboardRefreshInfo';
import DashboardCustomizationsTrigger from './components/DashboardCustomizations/DashboardCustomizationsTrigger';
import PermissionUserInfo from '../../../components/SharePermissionsWidget/PermissionUserInfo';
import DashboardNoContent from './components/DashboardNoContent';
import useUpdateDashboardStore from '../utils/useUpdateDashboardStore';
import DashboardContent from './components/DashboardContent';
import DashboardCustomizations from './components/DashboardCustomizations';
import toastMessage from '../../../utils/toastMessage';
import {
  dashboardUserRefreshed,
  dashboardViewed,
} from '../../../utils/analytics/events/dashboard/events';
import { useAnalytics } from '../../../utils/analytics/useAnalytics';
import DashboardInstanceModalController from './DashboardInstanceModalController';
import useVisitDashboardEffect from './utils/useVisitDashboardEffect';
import { FavoriteDashboardInstance } from '../../../components/Favorite';
import { dashboardAiScreenshotDivElementId } from '../../../components/ExplainWithAi';
import EntityRoleLabel from '../../../components/SharePermissionsWidget/EntityRoleLabel';
import DashboardBanner from './components/DashboardBanner';
import { entityRolesLabels } from '../../../utils/entityRoles';
import { ShareDashboardPermissionsWidget } from '../../../components/SharePermissionsWidget';
import useDashboardEditName from '../utils/edit/useDashboardEditName';
import useDashboardReload from '../utils/useDashboardReload';
import FullWidthDashboardAddContent from './FullWidthDashboardAddContent';
import DashboardInstanceWrapper from './DashboardInstanceWrapper';
import { selectEntityConversationThreads } from '../../../redux/selectors/conversationThreads';
import { EntityConversations } from '../../../components/Conversations';
import { disableCommenting } from '../../../redux/slices/conversationThreads';
import useOpenDashboardThreadEffect from './utils/useOpenDashboardThreadEffect';
import useTrackDashboardCommentCreate from '../utils/useTrackDashboardCommentCreate';

const DashboardInstance = () => {
  const dispatch = useAppDispatch();
  const updateDashboardStore = useUpdateDashboardStore();
  const { track } = useAnalytics();
  const trackNewComment = useTrackDashboardCommentCreate('dashboard');

  const dashboard = useDashboardSelector('dashboard');
  assertNotNil(dashboard);
  const {
    baseId,
    name,
    description,
    path,
    rows,
    refreshedAt,
    permission,
    isFavorite,
    createdAt,
    versionEditedAt,
    versionEditor,
    isLatestInstance,
    isDefault,
  } = dashboard;

  const conversationThreads = useAppSelector((state) =>
    selectEntityConversationThreads(
      state,
      ConversationThreadEntityType.Dashboard,
      baseId,
    ),
  );

  const reloadDashboard = useDashboardReload(dashboard.baseId);

  const {
    entityRole,
    dashboardEntityPermissions,
    dashboardEntityPermissionsHint,
  } = useDashboardEntityPermission(dashboard);

  const draftCustomization = useDashboardSelector('draftCustomization');
  const isCustomizationActive = draftCustomization.isActive;

  const allReportsLoaded = useAppSelector(areAllAnalysesLoadedSelector);

  const { editDashboardName } = useDashboardEditName(dashboard);

  const [refreshDashboard] = useMutation<
    IDashboardRefreshMutation,
    IDashboardRefreshMutationVariables
  >(DASHBOARD_REFRESH);

  const handleChangeName = async (newName: string) => {
    await editDashboardName({ newName });
  };

  const handleChangeDescription = async (newDescription: string) => {
    await editDashboardName({ newDescription });
  };

  const handleShowCustomizations = () => {
    dispatch(dashboardCustomizationActivate());
  };

  const handleRefreshDashboard = async () => {
    const { data } = await refreshDashboard({
      variables: {
        input: {
          baseId,
        },
      },
    });

    assertNotNil(data);
    const { dashboard: refreshedDashboard, errors } = data.dashboardRefresh;
    if (printGraphqlErrors(errors)) {
      await reloadDashboard();
      return;
    }
    assertNotNil(refreshedDashboard);
    updateDashboardStore({ dashboard: refreshedDashboard });

    track(dashboardUserRefreshed(refreshedDashboard));
    // we reload the dashboard after the update so it also constitutes as view
    track(dashboardViewed(refreshedDashboard));

    toastMessage({
      message: `Dashboard ${refreshedDashboard.name} is refreshed.`,
      type: 'info',
    });
  };

  const toggleFavorite = () => {
    dispatch(dashboardInstanceToggleFavorite());
  };

  useEffect(() => {
    if (!isDefault) {
      // Disable commenting in filtered dashboards
      dispatch(disableCommenting());
    }
  }, [dispatch, isDefault]);

  // Always scroll page to the top
  useEffect(() => {
    const mainContainer = document.getElementById('main-container');
    if (mainContainer) {
      mainContainer.scrollTo(0, 0);
    }
  }, [baseId]);

  // Record dashboard visit for Recent Activity
  useVisitDashboardEffect(baseId);

  // Open Conversation thread from search params
  useOpenDashboardThreadEffect();

  const showAddContent =
    !IS_SCREENSHOT_MODE &&
    dashboardEntityPermissions.editDashboard &&
    allReportsLoaded;
  const dashboardIsEmpty = !rows.length;

  return (
    <>
      {!isDefault ? (
        <DashboardBanner
          baseId={baseId}
          message={'Filters are applied, clear filters to edit'}
          buttonLabel={'Clear filters'}
        />
      ) : !isLatestInstance ? (
        <DashboardBanner
          baseId={baseId}
          message={
            'You are viewing a historical dashboard, view the latest version to edit'
          }
          buttonLabel={'View latest version'}
        />
      ) : null}

      <DashboardInstanceWrapper>
        <DashboardInstanceUI.Header>
          {path.length > 0 && <DashboardPagePath path={path} />}
          <DashboardInstanceUI.HeaderMain>
            <DashboardInstanceUI.NameWrapper>
              <EditableTextInput
                value={name}
                onSubmit={handleChangeName}
                disabled={!dashboardEntityPermissions.editDashboard}
                inputSize="L"
                limit={DASHBOARD_WORKSPACE_NAME_MAX_LENGTH}
              />
              <FavoriteDashboardInstance
                dashboardBaseId={baseId}
                isFavorite={isFavorite}
                onToggle={toggleFavorite}
              />
              {!IS_SCREENSHOT_MODE && conversationThreads.length > 0 && (
                <EntityConversations
                  entityId={baseId}
                  entityType={ConversationThreadEntityType.Dashboard}
                  threads={conversationThreads}
                  onAddNewComment={trackNewComment}
                />
              )}
            </DashboardInstanceUI.NameWrapper>
            <DashboardInstanceUI.HeaderActions>
              <PermissionUserInfo
                user={permission.owner}
                contentLabel={entityRolesLabels[EntityRole.Owner]}
              />
              {!moment(createdAt).isSame(versionEditedAt) && (
                <PermissionUserInfo
                  user={versionEditor}
                  contentLabel={entityRolesLabels[EntityRole.Editor]}
                />
              )}
              <DashboardInstanceUI.IconActions>
                <DashboardCustomizationsTrigger
                  onClick={handleShowCustomizations}
                  isActive={isCustomizationActive}
                  disabled={!allReportsLoaded || isCustomizationActive}
                  isLoading={!allReportsLoaded}
                />
                <DashboardOptions
                  onRefresh={handleRefreshDashboard}
                  dashboardBaseId={baseId}
                  dashboardEntityPermissions={dashboardEntityPermissions}
                  dashboardEntityPermissionsHint={
                    dashboardEntityPermissionsHint
                  }
                  isDisabled={!allReportsLoaded}
                />
              </DashboardInstanceUI.IconActions>
              <ShareDashboardPermissionsWidget />
            </DashboardInstanceUI.HeaderActions>
          </DashboardInstanceUI.HeaderMain>

          <DashboardInstanceUI.HeaderMain>
            <EditableTextInput
              value={description}
              onSubmit={handleChangeDescription}
              disabled={!dashboardEntityPermissions.editDashboard}
              limit={DESCRIPTION_MAX_LENGTH}
              placeholder="Enter dashboard description. This can help others better understand your dashboard"
              inputSize="XS"
              allowEmpty={true}
              multiline={true}
            />
            <DashboardInstanceUI.HeaderSecondaryInfo>
              <EntityRoleLabel entityRole={entityRole} />
              {refreshedAt && (
                <DashboardRefreshInfo refreshedAt={refreshedAt} />
              )}
            </DashboardInstanceUI.HeaderSecondaryInfo>
          </DashboardInstanceUI.HeaderMain>
        </DashboardInstanceUI.Header>

        {isCustomizationActive && (
          <>
            <DashboardCustomizations
              dashboardEntityPermissions={dashboardEntityPermissions}
            />
            <PageHeaderDivider />
          </>
        )}
        <DashboardInstanceUI.ContentWrapper
          $isEmpty={dashboardIsEmpty}
          // Do NOT remove this ID otherwise you will break the AI screenshots
          id={dashboardAiScreenshotDivElementId(baseId)}
        >
          {dashboardIsEmpty ? (
            <DashboardNoContent showAddContent={showAddContent} />
          ) : (
            <>
              <DashboardContent />
              {showAddContent && <FullWidthDashboardAddContent />}
            </>
          )}
        </DashboardInstanceUI.ContentWrapper>
      </DashboardInstanceWrapper>
      <DashboardInstanceModalController dashboardBaseId={baseId} />
    </>
  );
};

export default DashboardInstance;
